import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSiteConfig } from "@/contexts/SiteConfigContext";
import api from "@/lib/api";
import { SignalBars, Tape, formatDate, initials } from "@/components/site/parts";
import NotFound from "@/pages/NotFound";
import { useCopy } from "@/lib/copy";

export default function BlogPost() {
  const { slug } = useParams();
  const { config } = useSiteConfig();
  const t = useCopy();
  const [post, setPost] = useState(null);
  const [all, setAll] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPost(null);
    setError(null);
    api
      .get(`/blog/${slug}`)
      .then(({ data }) => setPost(data))
      .catch((e) => setError(e?.response?.status === 404 ? 404 : "Couldn't load this post. Try again in a moment."));
  }, [slug]);

  useEffect(() => {
    api.get("/blog").then(({ data }) => setAll(data)).catch(() => {});
  }, []);

  if (error === 404) return <NotFound />;
  if (error) return <section className="bx" style={{ paddingTop: 32 }}><p className="lede">{error}</p></section>;
  if (!post) return <section className="bx" style={{ paddingTop: 32 }}><p className="kicker">Loading post…</p></section>;

  // The list is newest first: "previous" is older, "next" is newer.
  const idx = all.findIndex((p) => p.slug === post.slug);
  const prev = idx >= 0 ? all[idx + 1] : null;
  const next = idx > 0 ? all[idx - 1] : null;

  return (
    <>
      <article className="bx" style={{ paddingTop: 40 }}>
        <div className="post">
          <Link to="/blog" className="mono rise" style={{ fontSize: 12, textTransform: "uppercase" }} data-testid="blog-back">{t("post.back")}</Link>
          <span className="mono rise d1" style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 10, fontSize: 12, color: "#6B6A63", textTransform: "uppercase" }}>
            <SignalBars height={20} />
            {[formatDate(post.published_at || post.created_at, { day: "2-digit", month: "short", year: "numeric" }), post.tags?.[0], `${post.read_minutes} min`].filter(Boolean).join(" · ")}
          </span>
          <h1 className="hero-h stretch post-h" style={{ animationDelay: ".2s" }}>{post.title}</h1>
          {post.excerpt && <p className="rise d3 post-dek">{post.excerpt}</p>}
          <Tape height={8} />
          {post.cover_image && <img src={post.cover_image} alt="" className="post-cover" />}
          <div className="article" dangerouslySetInnerHTML={{ __html: post.content_html }} />
          <div className="byline">
            <Avatar name={config.name} src={config.profile_image_url} />
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="disp" style={{ fontSize: 16, fontWeight: 800, textTransform: "uppercase" }}>
                {config.name}
              </span>
              <span style={{ fontSize: 14, color: "#4A4F5A" }}>
                {config.role} {t("post.byline")} <Link to="/about" style={{ color: "var(--blue)", fontWeight: 600 }}>More about me</Link>
              </span>
            </div>
          </div>
        </div>
      </article>
      {(prev || next) && (
        <nav aria-label="More posts" className="bx" style={{ paddingTop: 48 }}>
          <div className="g2">
            {prev ? (
              <Link to={`/blog/${prev.slug}`} className="project-card post-nav-card">
                <span className="mono" style={{ fontSize: 12, color: "#6B6A63" }}>← PREVIOUS POST</span>
                <span className="disp" style={{ fontSize: 20, fontWeight: 800 }}>{prev.title}</span>
              </Link>
            ) : <span />}
            {next && (
              <Link to={`/blog/${next.slug}`} className="project-card post-nav-card" style={{ textAlign: "right" }}>
                <span className="mono" style={{ fontSize: 12, color: "#6B6A63" }}>NEXT POST →</span>
                <span className="disp" style={{ fontSize: 20, fontWeight: 800 }}>{next.title}</span>
              </Link>
            )}
          </div>
        </nav>
      )}
    </>
  );
}

function Avatar({ name, src }) {
  const [failed, setFailed] = useState(false);
  return (
    <div style={{ flex: "none", width: 56, height: 56, borderRadius: 12, background: "#C9D5FF", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {!failed ? (
        <img src={src || "/profile.jpeg"} alt="" onError={() => setFailed(true)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <span className="disp" style={{ fontWeight: 900, color: "#3A4A8F" }}>{initials(name)}</span>
      )}
    </div>
  );
}
